import { useCallback } from 'react'
import { FormikErrors } from 'formik'
import { IReOpenFormModel } from '@user/components/Leads/ReOpenDialog/ReOpenLeadDialog'
import {
  getOpportunityDetail,
  reOpenOpportunity,
} from '@user/store/opportunity'
import { useAppDispatch } from '@sales-center/common'

interface IUseActionsProps {
  onClose: () => void
  opportunityId: number
  updateOppDetail?: boolean
}

export function useActions({
  onClose,
  updateOppDetail,
  opportunityId,
}: IUseActionsProps) {
  const dispatch = useAppDispatch()

  const handleReOpenPopupCancelClick = useCallback(() => {
    onClose()
  }, [onClose])

  const handleReOpenPopupOkButtonClick = useCallback(
    async (values: IReOpenFormModel) => {
      if (values.expectedClosingDate) {
        await dispatch(
          reOpenOpportunity({
            id: opportunityId,
            expectedClosingDate: values.expectedClosingDate,
            owner: values.owner?.id || null,
          }),
        )
        if (updateOppDetail) await dispatch(getOpportunityDetail(opportunityId))
      }

      onClose()
    },
    [onClose, dispatch, opportunityId, updateOppDetail],
  )

  const validate = useCallback((model: IReOpenFormModel) => {
    const errors: FormikErrors<IReOpenFormModel> = {}

    const { expectedClosingDate, owner } = model
    if (
      expectedClosingDate === null ||
      isNaN(new Date(expectedClosingDate).getTime())
    ) {
      errors.expectedClosingDate = 'Required'
    }
    if (owner === null) {
      errors.owner = 'Required'
    }

    return errors
  }, [])

  const onSubmitForm = useCallback(
    (submitForm: () => Promise<void>, isValid: boolean, setFormikState) => {
      if (isValid) {
        submitForm()
      } else {
        setFormikState((prevState) => ({
          ...prevState,
          submitCount: prevState.submitCount + 1,
          touched: {
            ...prevState.touched,
            expectedClosingDate: true,
            owner: true,
          },
        }))
      }
    },
    [],
  )

  return {
    validate,
    handleReOpenPopupCancelClick,
    handleReOpenPopupOkButtonClick,
    onSubmitForm,
  }
}
